import { useTranslation } from 'react-i18next'

function CaptionPreferencesSection({
  emojiPreference,
  hashtagPreference,
  onEmojiPreferenceChange,
  onHashtagPreferenceChange
}) {
  const { t } = useTranslation('settings')

  const EMOJI_PREFERENCES = [
    {
      value: 'none',
      label: t('captions.emoji.none.label'),
      icon: '🚫',
      description: t('captions.emoji.none.description')
    },
    {
      value: 'sparse',
      label: t('captions.emoji.sparse.label'),
      icon: '😊',
      description: t('captions.emoji.sparse.description')
    },
    {
      value: 'moderate',
      label: t('captions.emoji.moderate.label'),
      icon: '😊✨',
      description: t('captions.emoji.moderate.description')
    },
    {
      value: 'generous',
      label: t('captions.emoji.generous.label'),
      icon: '😊✨🎉💫',
      description: t('captions.emoji.generous.description')
    }
  ]

  const HASHTAG_PREFERENCES = [
    {
      value: 'none',
      label: t('captions.hashtag.none.label'),
      icon: '🚫',
      description: t('captions.hashtag.none.description')
    },
    {
      value: 'minimal',
      label: t('captions.hashtag.minimal.label'),
      icon: '#',
      description: t('captions.hashtag.minimal.description')
    },
    {
      value: 'moderate',
      label: t('captions.hashtag.moderate.label'),
      icon: '##',
      description: t('captions.hashtag.moderate.description')
    },
    {
      value: 'many',
      label: t('captions.hashtag.many.label'),
      icon: '###',
      description: t('captions.hashtag.many.description')
    }
  ]

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{t('captions.title')}</h2>
        <p className="text-gray-600 mt-1">
          {t('captions.description')}
        </p>
      </div>

      {/* Emoji Preference */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t('captions.emojiUsageTitle')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {EMOJI_PREFERENCES.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onEmojiPreferenceChange(option.value)}
              className={`
                relative rounded-lg border-2 p-4 text-center transition-all
                ${emojiPreference === option.value
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
            >
              <div className="space-y-2">
                <div className="text-2xl">{option.icon}</div>
                <p className={`text-sm font-medium ${
                  emojiPreference === option.value ? 'text-primary-900' : 'text-gray-900'
                }`}>
                  {option.label}
                </p>
                <p className="text-xs text-gray-500">{option.description}</p>
              </div>
              {emojiPreference === option.value && (
                <div className="absolute top-2 right-2">
                  <svg className="h-5 w-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Hashtag Preference */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t('captions.hashtagUsageTitle')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {HASHTAG_PREFERENCES.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onHashtagPreferenceChange(option.value)}
              className={`
                relative rounded-lg border-2 p-4 text-center transition-all
                ${hashtagPreference === option.value
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
            >
              <div className="space-y-2">
                <div className="text-2xl font-mono">{option.icon}</div>
                <p className={`text-sm font-medium ${
                  hashtagPreference === option.value ? 'text-primary-900' : 'text-gray-900'
                }`}>
                  {option.label}
                </p>
                <p className="text-xs text-gray-500">{option.description}</p>
              </div>
              {hashtagPreference === option.value && (
                <div className="absolute top-2 right-2">
                  <svg className="h-5 w-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium">{t('captions.infoTitle')}</p>
            <p className="text-blue-700 mt-1">
              {t('captions.infoDescription')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CaptionPreferencesSection
